import React, { useState, useEffect, useMemo, useCallback } from 'react'
import {
  Modal,
  Form,
  Input,
  Button,
  Tabs,
  Card,
  Empty,
  Popconfirm,
  List,
  message,
} from 'antd'
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  MenuOutlined,
} from '@ant-design/icons'
import styled from 'styled-components'
import { useDispatch, useSelector } from 'react-redux'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import {
  fetchMenuDetails,
  createMenuSection,
  updateMenuSection,
  deleteMenuSection,
  fetchAvailableRecipes,
  addRecipesToMenu,
  removeRecipeFromMenu,
} from 'src/features/MenuBuilderSlice'
import allergenEventEmitter from 'src/utils/allergenEventEmitter'
import menuBuilderMenusCopy from 'src/constants/menuBuilderCopy'
import {
  buildMenuSalesMixHeaderMetrics,
  buildMenuSalesMixPanelRecipes,
} from 'src/utils/menuSalesMixPanelData'
import { calculateSellingPriceInclVatFormatted } from 'src/utils/vatCalculations'
import { getGpBandChipStyle } from 'src/utils/recipeGp'
import MenuSalesMixTab from './MenuSalesMixTab'
import GpBandChip from './GpBandChip'
import {
  countMonitoredMenuRecipes,
  isRecipeArchived,
  recipeShowsMenuEconomics,
} from 'src/utils/recipeMonitoring'
import AddRecipePickerListItem from './AddRecipePickerListItem'

export const EDIT_MENU_TAB = {
  DETAILS: 'details',
  RECIPES: 'recipes',
  SALES_MIX: 'salesMix',
}

const headerChipStyles = {
  weighted: {
    color: '#ad6800',
    backgroundColor: '#fff7e6',
    border: '1px solid #ffd591',
  },
  revenue: {
    color: '#0958d9',
    backgroundColor: '#e6f4ff',
    border: '1px solid #91caff',
  },
}

const EditMenuHeaderChip = ({ variant, gp, children }) => {
  const variantStyle =
    variant === 'avg' && gp != null
      ? getGpBandChipStyle(gp)
      : headerChipStyles[variant]

  return (
    <span
      style={{
        fontWeight: 'normal',
        fontSize: '12px',
        padding: '2px 6px',
        borderRadius: '4px',
        ...variantStyle,
      }}
    >
      {children}
    </span>
  )
}

const getRecipeGpPercent = recipe => {
  if (isRecipeArchived(recipe)) {
    return null
  }

  if (!recipe?.sellingPrice || !recipe?.costPerPortion) {
    return null
  }

  if (recipe.grossProfit && !isNaN(parseFloat(recipe.grossProfit))) {
    return parseFloat(recipe.grossProfit)
  }

  return (
    ((recipe.sellingPrice - recipe.costPerPortion) / recipe.sellingPrice) * 100
  )
}

const { TextArea } = Input
const { TabPane } = Tabs

const ModalContent = styled.div`
  .ant-tabs-content-holder {
    padding: 0;
  }

  .ant-tabs-ink-bar {
    transition: none !important;
    animation: none !important;
  }

  .ant-tabs-content-holder {
    transition: none !important;
  }
`

const SectionCard = styled(Card)`
  margin-bottom: 16px;
  transition: all 0.2s ease;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }

  .section-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;
  }

  .section-name {
    font-size: 16px;
    font-weight: 600;
  }

  .section-actions {
    display: flex;
    gap: 8px;
  }

  .drag-handle {
    cursor: grab;
    padding: 4px;
    display: flex;
    alignitems: center;
    color: #999;
    font-size: 16px;
    transition: color 0.2s ease;

    &:hover {
      color: #666;
    }

    &:active {
      cursor: grabbing;
    }
  }
`

const SectionDivider = styled.div`
  height: 2px;
  background: linear-gradient(90deg, #e8e8e8 0%, #d9d9d9 50%, #e8e8e8 100%);
  margin: 24px 0;
  border-radius: 1px;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: -1px;
    left: 50%;
    transform: translateX(-50%);
    width: 40px;
    height: 4px;
    background: #f0f0f0;
    border-radius: 2px;
  }
`

// Sortable Section Component
const SortableSection = ({ section, children }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: section._id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  return (
    <div ref={setNodeRef} style={style}>
      {children({
        dragAttributes: attributes,
        dragListeners: listeners,
      })}
    </div>
  )
}

const EditMenuModal = ({
  visible,
  menu,
  onCancel,
  onOk,
  form,
  venueRecipes = [],
}) => {
  const [loading, setLoading] = useState(false)
  const [activeTab, setActiveTab] = useState('details')
  const [localSections, setLocalSections] = useState([])

  // Sensors for drag and drop
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )

  // Simple state for Add Section modal
  const [showAddSection, setShowAddSection] = useState(false)
  const [sectionName, setSectionName] = useState('')
  const [isCreatingSection, setIsCreatingSection] = useState(false)

  // State for Rename Section modal
  const [showRenameSection, setShowRenameSection] = useState(false)
  const [renamingSection, setRenamingSection] = useState(null)
  const [newSectionName, setNewSectionName] = useState('')
  const [isRenamingSection, setIsRenamingSection] = useState(false)

  // Recipe management state
  const [showAddRecipes, setShowAddRecipes] = useState(false)
  const [selectedRecipes, setSelectedRecipes] = useState([])
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedSection, setSelectedSection] = useState(null)
  const [salesMixLiveMetrics, setSalesMixLiveMetrics] = useState(null)

  const dispatch = useDispatch()
  const { currentMenu, menuSections, availableRecipes, menuRecipes } =
    useSelector(state => state.menus)

  // Update local sections when menu data changes
  useEffect(() => {
    const sectionsToShow = currentMenu?.sections || menuSections || []
    setLocalSections(sectionsToShow)
  }, [currentMenu?.sections, menuSections])

  useEffect(() => {
    if (visible && menu) {
      form.setFieldsValue({
        name: menu.name,
        description: menu.description,
      })
      dispatch(fetchMenuDetails(menu._id))
    }
  }, [visible, menu])

  // Listen for recipe updates to refresh available recipes
  useEffect(() => {
    if (visible && currentMenu?._id) {
      // Refresh available recipes when the modal is visible and menu is loaded
      dispatch(fetchAvailableRecipes(currentMenu._id))
    }
  }, [visible, currentMenu?._id, dispatch])

  // Listen for allergen update events to refresh menu data
  useEffect(() => {
    const handleAllergenUpdate = () => {
      if (visible && currentMenu?._id) {
        dispatch(fetchMenuDetails(currentMenu._id))
        dispatch(fetchAvailableRecipes(currentMenu._id))
      }
    }

    allergenEventEmitter.on('allergen-updated', handleAllergenUpdate)

    return () => {
      allergenEventEmitter.off('allergen-updated', handleAllergenUpdate)
    }
  }, [visible, currentMenu?._id, dispatch])

  // Add escape key handler to close modals
  useEffect(() => {
    const handleEscape = e => {
      if (e.key === 'Escape') {
        if (showAddSection) {
          setShowAddSection(false)
          setSectionName('')
        }
      }
    }

    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [showAddSection])

  // Reset state when modal closes
  useEffect(() => {
    if (!visible) {
      setShowAddSection(false)
      setSectionName('')
      setActiveTab('details')
      setIsCreatingSection(false)
      setSalesMixLiveMetrics(null)
    }
  }, [visible])

  useEffect(() => {
    if (activeTab !== EDIT_MENU_TAB.SALES_MIX) {
      setSalesMixLiveMetrics(null)
    }
  }, [activeTab])

  const handleSalesMixPreviewChange = useCallback(metrics => {
    setSalesMixLiveMetrics(prev => {
      if (
        prev?.hasSalesMix === metrics?.hasSalesMix &&
        prev?.weightedGp === metrics?.weightedGp &&
        prev?.estimatedTotalGp === metrics?.estimatedTotalGp
      ) {
        return prev
      }

      return metrics
    })
  }, [])

  const handleOk = async () => {
    try {
      setLoading(true)
      const values = await form.validateFields()
      await onOk(menu._id, values)
    } catch (error) {
      console.error('Validation failed:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleCancel = () => {
    form.resetFields()
    setActiveTab('details')
    setShowAddSection(false)
    setSectionName('')
    onCancel()
  }

  // Simple Add Section handlers
  const handleOpenAddSection = () => {
    setShowAddSection(true)
    setSectionName('')
  }

  const handleCloseAddSection = () => {
    setShowAddSection(false)
    setSectionName('')
  }

  const handleCreateSection = async () => {
    if (!sectionName.trim()) {
      return
    }

    if (!currentMenu?._id) {
      return
    }

    if (isCreatingSection) {
      return
    }

    try {
      setIsCreatingSection(true)
      await dispatch(
        createMenuSection(currentMenu._id, { name: sectionName.trim() }),
      )
      await dispatch(fetchMenuDetails(currentMenu._id))
      setShowAddSection(false)
      setSectionName('')
    } catch (error) {
      console.error('Error creating section:', error)
      setShowAddSection(false)
      setSectionName('')
    } finally {
      setIsCreatingSection(false)
    }
  }

  const handleOpenRenameSection = section => {
    setRenamingSection(section)
    setNewSectionName(section.name)
    setShowRenameSection(true)
  }

  const handleCloseRenameSection = () => {
    setShowRenameSection(false)
    setRenamingSection(null)
    setNewSectionName('')
  }

  const handleRenameSection = async () => {
    if (!newSectionName.trim() || !renamingSection) {
      return
    }

    if (isRenamingSection) {
      return
    }

    try {
      setIsRenamingSection(true)
      await dispatch(
        updateMenuSection(
          renamingSection._id,
          { name: newSectionName.trim() },
          currentMenu._id,
        ),
      )
      setShowRenameSection(false)
      setRenamingSection(null)
      setNewSectionName('')
    } catch (error) {
      console.error('Error renaming section:', error)
      setShowRenameSection(false)
      setRenamingSection(null)
      setNewSectionName('')
    } finally {
      setIsRenamingSection(false)
    }
  }

  const handleDeleteSection = async sectionId => {
    await dispatch(deleteMenuSection(sectionId, currentMenu._id))
  }

  // Recipe management functions
  const handleOpenAddRecipes = sectionId => {
    setSelectedSection(sectionId)
    setShowAddRecipes(true)
    setSelectedRecipes([])
    setSearchTerm('')
    // console.log('Opening Add Recipes modal for section:', sectionId)
    // console.log('Current menu ID:', currentMenu._id)
    // Always fetch fresh available recipes when opening the modal
    dispatch(fetchAvailableRecipes(currentMenu._id))
  }

  const handleCloseAddRecipes = () => {
    setShowAddRecipes(false)
    setSelectedRecipes([])
    setSearchTerm('')
    setSelectedSection(null)
  }

  const handleAddRecipesToSection = async () => {
    if (selectedRecipes.length === 0) {
      message.warning('Please select at least one recipe')
      return
    }

    try {
      await dispatch(
        addRecipesToMenu(currentMenu._id, selectedRecipes, selectedSection),
      )
      message.success('Recipes added successfully')
      handleCloseAddRecipes()
    } catch (error) {
      message.error('Failed to add recipes')
    }
  }

  const handleRemoveRecipe = async (sectionId, recipeId) => {
    try {
      await dispatch(removeRecipeFromMenu(currentMenu._id, sectionId, recipeId))
      message.success('Recipe removed successfully')
    } catch (error) {
      message.error('Failed to remove recipe')
    }
  }

  // Drag and drop handler for sections - MVP version (local state only)
  const handleDragEnd = event => {
    const { active, over } = event

    if (active.id !== over.id) {
      const oldIndex = localSections.findIndex(
        section => section._id === active.id,
      )
      const newIndex = localSections.findIndex(
        section => section._id === over.id,
      )

      const items = arrayMove(localSections, oldIndex, newIndex)

      // Update local state to show the reorder visually
      setLocalSections(items)
      message.success('Sections reordered (local changes only)')
    }
  }

  const applyVenueMonitoring = recipe => {
    if (!recipe?._id) return recipe
    if (recipe.monitored === false || recipe.monitored === true) return recipe
    const fromVenue = (venueRecipes || []).find(
      item => String(item?._id) === String(recipe._id),
    )
    if (fromVenue?.monitored === false) {
      return { ...recipe, monitored: false }
    }
    return recipe
  }

  const getRecipesBySection = sectionId => {
    // First try to get recipes from recipesBySection object (new structure)
    if (currentMenu?.recipesBySection?.[sectionId]?.recipes) {
      return currentMenu.recipesBySection[sectionId].recipes.map(recipe => ({
        recipe: applyVenueMonitoring(recipe),
        section: sectionId,
      }))
    }

    // Try to get recipes directly from section object
    const section = (currentMenu?.sections || menuSections || []).find(
      s => s._id === sectionId,
    )
    if (section?.recipes && section.recipes.length > 0) {
      return section.recipes.map(recipe => ({
        recipe: applyVenueMonitoring(recipe),
        section: sectionId,
      }))
    }

    // Fall back to the old flat menuRecipes array
    return menuRecipes
      .filter(mr => mr.section === sectionId)
      .map(mr => ({
        ...mr,
        recipe: applyVenueMonitoring(mr.recipe),
      }))
  }

  const calculateSectionGP = sectionId => {
    const recipes = getRecipesBySection(sectionId)
    if (recipes.length === 0) return 0

    const validRecipes = recipes.filter(
      r =>
        !isRecipeArchived(r.recipe) &&
        r.recipe.sellingPrice &&
        r.recipe.costPerPortion,
    )
    if (validRecipes.length === 0) return 0

    const totalGP = validRecipes.reduce((sum, r) => {
      // Use pre-calculated grossProfit if available, otherwise calculate it
      let gp = 0
      if (r.recipe.grossProfit && !isNaN(parseFloat(r.recipe.grossProfit))) {
        gp = parseFloat(r.recipe.grossProfit)
      } else {
        gp =
          ((r.recipe.sellingPrice - r.recipe.costPerPortion) /
            r.recipe.sellingPrice) *
          100
      }
      return sum + gp
    }, 0)

    return Math.round(totalGP / validRecipes.length)
  }

  const calculateMenuGP = () => {
    // Collect all recipes from all sections
    let allRecipes = []

    // Try new data structure first (recipesBySection)
    if (currentMenu?.recipesBySection) {
      Object.values(currentMenu.recipesBySection).forEach(sectionData => {
        if (sectionData.recipes) {
          allRecipes.push(
            ...sectionData.recipes.map(recipe => applyVenueMonitoring(recipe)),
          )
        }
      })
    }

    // If no recipes found, try section.recipes
    if (allRecipes.length === 0 && currentMenu?.sections) {
      currentMenu.sections.forEach(section => {
        if (section.recipes) {
          allRecipes.push(
            ...section.recipes.map(recipe => applyVenueMonitoring(recipe)),
          )
        }
      })
    }

    // Fall back to old menuRecipes array structure
    if (allRecipes.length === 0 && menuRecipes && menuRecipes.length > 0) {
      allRecipes = menuRecipes.map(mr => applyVenueMonitoring(mr.recipe))
    }

    if (allRecipes.length === 0) return 0

    const validRecipes = allRecipes.filter(
      r => !isRecipeArchived(r) && r.sellingPrice && r.costPerPortion,
    )
    if (validRecipes.length === 0) return 0

    const totalGP = validRecipes.reduce((sum, r) => {
      // Use pre-calculated grossProfit if available, otherwise calculate it
      let gp = 0
      if (r.grossProfit && !isNaN(parseFloat(r.grossProfit))) {
        gp = parseFloat(r.grossProfit)
      } else {
        gp = ((r.sellingPrice - r.costPerPortion) / r.sellingPrice) * 100
      }
      return sum + gp
    }, 0)

    return Math.round(totalGP / validRecipes.length)
  }

  const salesMixSections =
    localSections.length > 0
      ? localSections
      : currentMenu?.sections || menuSections || []

  const panelRecipes = useMemo(
    () =>
      buildMenuSalesMixPanelRecipes({
        sections: salesMixSections,
        getRecipesBySection,
      }),
    [
      salesMixSections,
      currentMenu?.recipesBySection,
      currentMenu?.sections,
      menuSections,
      menuRecipes,
      venueRecipes,
    ],
  )

  const isSalesMixTab = activeTab === EDIT_MENU_TAB.SALES_MIX
  const isDetailsTab = activeTab === EDIT_MENU_TAB.DETAILS
  const showModalSave = isDetailsTab
  const salesMixMenuId = currentMenu?._id || menu?._id

  const salesMixInitial = useMemo(() => {
    if (currentMenu?._id && String(currentMenu._id) === String(menu?._id)) {
      return currentMenu.salesMix || []
    }

    return menu?.salesMix || []
  }, [currentMenu?._id, currentMenu?.salesMix, menu?._id, menu?.salesMix])

  const headerGpChips = useMemo(() => {
    const avgGp = calculateMenuGP()
    const salesMix = buildMenuSalesMixHeaderMetrics({
      isSalesMixTab,
      liveMetrics: salesMixLiveMetrics,
      currentMenu,
      menu,
    })

    return {
      avgGp: avgGp > 0 ? avgGp : null,
      weightedGp: salesMix.weightedGp,
      estimatedTotalGpLabel: salesMix.estimatedTotalGpLabel,
    }
  }, [
    currentMenu,
    isSalesMixTab,
    menu,
    salesMixLiveMetrics,
    currentMenu?.estimatedTotalGp,
    currentMenu?.hasSalesMix,
    currentMenu?.weightedGp,
    menu?.estimatedTotalGp,
    menu?.hasSalesMix,
    menu?.weightedGp,
    menuRecipes,
    localSections,
  ])

  if (!menu) return null

  return (
    <>
      <Modal
        title={
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span>Edit Menu: {menu.name}</span>
            </div>
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '6px',
                marginTop: 8,
              }}
            >
              {headerGpChips.avgGp != null ? (
                <EditMenuHeaderChip variant='avg' gp={headerGpChips.avgGp}>
                  {menuBuilderMenusCopy.menuHeaderAvgGpLabel.replace(
                    '{gp}',
                    String(headerGpChips.avgGp),
                  )}
                </EditMenuHeaderChip>
              ) : null}
              {isSalesMixTab && headerGpChips.weightedGp != null ? (
                <EditMenuHeaderChip variant='weighted'>
                  {menuBuilderMenusCopy.menuCardWeightedGpLabel.replace(
                    '{gp}',
                    String(headerGpChips.weightedGp),
                  )}
                </EditMenuHeaderChip>
              ) : null}
              {isSalesMixTab && headerGpChips.estimatedTotalGpLabel ? (
                <EditMenuHeaderChip variant='revenue'>
                  {menuBuilderMenusCopy.menuCardEstGpLabel.replace(
                    '{amount}',
                    headerGpChips.estimatedTotalGpLabel,
                  )}
                </EditMenuHeaderChip>
              ) : null}
            </div>
          </div>
        }
        visible={visible}
        onOk={showModalSave ? handleOk : undefined}
        onCancel={handleCancel}
        confirmLoading={loading && showModalSave}
        okText='Save Changes'
        cancelText='Cancel'
        footer={
          showModalSave
            ? undefined
            : [
                <Button key='cancel' onClick={handleCancel}>
                  Cancel
                </Button>,
              ]
        }
        width={800}
        style={{ top: 20 }}
        maskClosable={false}
      >
        <ModalContent>
          <Tabs activeKey={activeTab} onChange={setActiveTab} animated={false}>
            <TabPane tab='Details' key='details'>
              <Form layout='vertical'>
                <Form.Item
                  label='Menu Name'
                  rules={[
                    { required: true, message: 'Please enter a menu name' },
                    {
                      min: 2,
                      message: 'Menu name must be at least 2 characters',
                    },
                    {
                      max: 80,
                      message: 'Menu name must be less than 80 characters',
                    },
                  ]}
                >
                  {form.getFieldDecorator('name', {
                    rules: [
                      { required: true, message: 'Please enter a menu name' },
                      {
                        min: 2,
                        message: 'Menu name must be at least 2 characters',
                      },
                      {
                        max: 80,
                        message: 'Menu name must be less than 80 characters',
                      },
                    ],
                  })(
                    <Input
                      placeholder='e.g., Lunch Menu, Dinner Menu, Kids Menu'
                      maxLength={80}
                    />,
                  )}
                </Form.Item>

                <Form.Item label='Description (Optional)'>
                  {form.getFieldDecorator('description', {
                    rules: [
                      {
                        max: 500,
                        message: 'Description must be less than 500 characters',
                      },
                    ],
                  })(
                    <TextArea
                      placeholder='Add a description for this menu...'
                      rows={3}
                      maxLength={500}
                    />,
                  )}
                </Form.Item>
              </Form>
            </TabPane>

            <TabPane tab='Sections & Recipes' key='recipes'>
              <div>
                <div
                  style={{
                    marginBottom: 16,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <h3>Sections</h3>
                  <Button type='primary' onClick={handleOpenAddSection}>
                    <PlusOutlined /> Add Section
                  </Button>
                </div>

                {(() => {
                  return localSections.length > 0 ? (
                    <DndContext
                      sensors={sensors}
                      collisionDetection={closestCenter}
                      onDragEnd={handleDragEnd}
                    >
                      <SortableContext
                        items={localSections.map(section => section._id)}
                        strategy={verticalListSortingStrategy}
                      >
                        {localSections.map((section, index) => (
                          <React.Fragment key={section._id}>
                            {index > 0 && <SectionDivider />}
                            <SortableSection section={section}>
                              {dragProps => (
                                <div
                                  style={{
                                    marginBottom: '16px',
                                  }}
                                >
                                  <SectionCard size='small'>
                                    <div className='section-header'>
                                      <div
                                        className='section-name'
                                        style={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: '8px',
                                        }}
                                      >
                                        <div
                                          className='drag-handle'
                                          {...dragProps.dragAttributes}
                                          {...dragProps.dragListeners}
                                          style={{
                                            cursor: 'grab',
                                            padding: '4px',
                                            borderRadius: '4px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                          }}
                                        >
                                          <MenuOutlined />
                                        </div>
                                        <div>
                                          {section.name}
                                          {(() => {
                                            const sectionGp =
                                              calculateSectionGP(section._id)
                                            if (sectionGp <= 0) return null

                                            return (
                                              <GpBandChip
                                                gp={sectionGp}
                                                style={{
                                                  fontWeight: 'normal',
                                                  marginLeft: '8px',
                                                }}
                                              >
                                                Avg: {sectionGp}% GP
                                              </GpBandChip>
                                            )
                                          })()}
                                        </div>
                                      </div>
                                      <div className='section-actions'>
                                        <Button
                                          type='link'
                                          onClick={e => {
                                            e.stopPropagation()
                                            handleOpenRenameSection(section)
                                          }}
                                        >
                                          <EditOutlined /> Rename
                                        </Button>
                                        <Popconfirm
                                          title='Delete section?'
                                          description="Recipes in this section will be moved to 'Unassigned'"
                                          onConfirm={() =>
                                            handleDeleteSection(section._id)
                                          }
                                        >
                                          <Button
                                            type='link'
                                            danger
                                            onClick={e => e.stopPropagation()}
                                          >
                                            <DeleteOutlined /> Delete
                                          </Button>
                                        </Popconfirm>
                                      </div>
                                    </div>

                                    <div>
                                      <div
                                        style={{
                                          display: 'flex',
                                          justifyContent: 'space-between',
                                          alignItems: 'center',
                                          marginBottom: 8,
                                        }}
                                      >
                                        <h4>
                                          Recipes (
                                          {countMonitoredMenuRecipes(
                                            getRecipesBySection(section._id),
                                          )}
                                          )
                                        </h4>
                                        <Button
                                          type='link'
                                          onClick={e => {
                                            e.stopPropagation()
                                            handleOpenAddRecipes(section._id)
                                          }}
                                        >
                                          <PlusOutlined /> Add Recipes
                                        </Button>
                                      </div>

                                      {getRecipesBySection(section._id).length >
                                      0 ? (
                                        <List
                                          size='small'
                                          dataSource={getRecipesBySection(
                                            section._id,
                                          )}
                                          renderItem={menuRecipe => (
                                            <List.Item
                                              actions={[
                                                <Button
                                                  key='remove'
                                                  type='link'
                                                  danger
                                                  size='small'
                                                  onClick={() =>
                                                    handleRemoveRecipe(
                                                      section._id,
                                                      menuRecipe.recipe._id,
                                                    )
                                                  }
                                                >
                                                  Remove
                                                </Button>,
                                              ]}
                                            >
                                              <List.Item.Meta
                                                title={
                                                  <div
                                                    style={{
                                                      display: 'flex',
                                                      justifyContent:
                                                        'space-between',
                                                      alignItems: 'center',
                                                    }}
                                                  >
                                                    <span>
                                                      {menuRecipe.recipe.title}
                                                    </span>
                                                    {recipeShowsMenuEconomics(
                                                      menuRecipe.recipe,
                                                    ) &&
                                                      menuRecipe.recipe
                                                        .sellingPrice && (
                                                        <div
                                                          style={{
                                                            display: 'flex',
                                                            alignItems:
                                                              'center',
                                                            gap: '8px',
                                                          }}
                                                        >
                                                          <span
                                                            style={{
                                                              color: '#000',
                                                              fontWeight:
                                                                'bold',
                                                              fontSize: '14px',
                                                            }}
                                                          >
                                                            Margin £
                                                            {Number(
                                                              menuRecipe.recipe
                                                                .sellingPrice,
                                                            ).toFixed(2)}
                                                          </span>
                                                          <span
                                                            style={{
                                                              color: '#666',
                                                              fontSize: '12px',
                                                            }}
                                                          >
                                                            Sell £
                                                            {calculateSellingPriceInclVatFormatted(
                                                              menuRecipe.recipe
                                                                .sellingPrice,
                                                              Boolean(
                                                                menuRecipe
                                                                  .recipe
                                                                  .hasVat,
                                                              ),
                                                            ) ||
                                                              Number(
                                                                menuRecipe
                                                                  .recipe
                                                                  .sellingPrice,
                                                              ).toFixed(2)}
                                                          </span>
                                                          {menuRecipe.recipe
                                                            .costPerPortion &&
                                                            !isRecipeArchived(
                                                              menuRecipe.recipe,
                                                            ) && (
                                                              <GpBandChip
                                                                gp={getRecipeGpPercent(
                                                                  menuRecipe.recipe,
                                                                )}
                                                              >
                                                                {Math.round(
                                                                  getRecipeGpPercent(
                                                                    menuRecipe.recipe,
                                                                  ) || 0,
                                                                )}
                                                                % GP
                                                              </GpBandChip>
                                                            )}
                                                        </div>
                                                      )}
                                                  </div>
                                                }
                                                description={
                                                  <div>
                                                    {menuRecipe.recipe
                                                      .description && (
                                                      <div
                                                        style={{
                                                          marginBottom: '4px',
                                                        }}
                                                      >
                                                        {
                                                          menuRecipe.recipe
                                                            .description
                                                        }
                                                      </div>
                                                    )}
                                                  </div>
                                                }
                                              />
                                            </List.Item>
                                          )}
                                        />
                                      ) : (
                                        <Empty
                                          description='No recipes in this section yet'
                                          image={Empty.PRESENTED_IMAGE_SIMPLE}
                                        />
                                      )}
                                    </div>
                                  </SectionCard>
                                </div>
                              )}
                            </SortableSection>
                          </React.Fragment>
                        ))}
                      </SortableContext>
                    </DndContext>
                  ) : (
                    <Empty
                      description='No sections yet. Add your first section!'
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                    />
                  )
                })()}
              </div>
            </TabPane>

            <TabPane
              tab={menuBuilderMenusCopy.salesMixTab}
              key={EDIT_MENU_TAB.SALES_MIX}
            >
              {isSalesMixTab && salesMixMenuId ? (
                <MenuSalesMixTab
                  key={salesMixMenuId}
                  menuId={salesMixMenuId}
                  panelRecipes={panelRecipes}
                  initialSalesMix={salesMixInitial}
                  onPreviewChange={handleSalesMixPreviewChange}
                />
              ) : null}
            </TabPane>
          </Tabs>
        </ModalContent>
      </Modal>

      {/* Simple Add Section Modal */}
      <Modal
        title='Add New Section'
        visible={showAddSection}
        onOk={handleCreateSection}
        onCancel={handleCloseAddSection}
        okText='Add Section'
        cancelText='Cancel'
        confirmLoading={isCreatingSection}
        width={400}
        destroyOnClose={true}
        maskClosable={false}
      >
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
            Section Name
          </label>
          <input
            type='text'
            placeholder='e.g., Starters, Mains, Desserts'
            value={sectionName}
            onChange={e => {
              const value = e.target.value
              setSectionName(value)
            }}
            onKeyPress={e => {
              if (e.key === 'Enter' && !isCreatingSection) {
                handleCreateSection()
              }
            }}
            disabled={isCreatingSection}
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '1px solid #d9d9d9',
              borderRadius: '6px',
              fontSize: '14px',
              outline: 'none',
              opacity: isCreatingSection ? 0.6 : 1,
            }}
            autoFocus
          />
        </div>
      </Modal>

      {/* Add Recipes Modal */}
      <Modal
        title='Add Recipes to Section'
        visible={showAddRecipes}
        onOk={handleAddRecipesToSection}
        onCancel={handleCloseAddRecipes}
        okText='Add Recipes'
        cancelText='Cancel'
        width={720}
        destroyOnClose={true}
        maskClosable={false}
      >
        <div style={{ marginBottom: 16 }}>
          <Input
            placeholder='Search recipes...'
            prefix={<SearchOutlined />}
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            style={{ marginBottom: 16 }}
          />

          <div style={{ maxHeight: 400, overflowY: 'auto' }}>
            <List
              dataSource={availableRecipes.filter(
                recipe =>
                  recipe &&
                  recipe.title &&
                  recipe.title
                    .toLowerCase()
                    .includes((searchTerm || '').toLowerCase()),
              )}
              renderItem={recipe => (
                <AddRecipePickerListItem
                  recipe={recipe}
                  checked={selectedRecipes.includes(recipe._id)}
                  onCheckedChange={e => {
                    if (e.target.checked) {
                      setSelectedRecipes([...selectedRecipes, recipe._id])
                    } else {
                      setSelectedRecipes(
                        selectedRecipes.filter(id => id !== recipe._id),
                      )
                    }
                  }}
                />
              )}
            />
          </div>
        </div>
      </Modal>

      {/* Rename Section Modal */}
      <Modal
        title='Rename Section'
        visible={showRenameSection}
        onOk={handleRenameSection}
        onCancel={handleCloseRenameSection}
        confirmLoading={isRenamingSection}
        okText='Rename Section'
        cancelText='Cancel'
        maskClosable={false}
      >
        <Input
          type='text'
          value={newSectionName}
          onChange={e => setNewSectionName(e.target.value)}
          placeholder='Enter new section name'
          onPressEnter={handleRenameSection}
          disabled={isRenamingSection}
          maxLength={80}
        />
      </Modal>
    </>
  )
}

export default Form.create({ name: 'edit_menu' })(EditMenuModal)
